/**
 * What the memo page's Analyst rail sends with every turn (DOP-499).
 *
 * A *descriptor*, never data: only `run_id` identifies anything, and the
 * server re-reads that run owner-scoped at turn time and stages its memo and
 * facts. `tab` and `section_key` say where on the page the question was asked,
 * so "this section" means one section and the thread reopens where it was
 * asked. Pinned by `DealScreeningContextRequest` in `chats/schemas/stream.py`.
 *
 * `draft` is the one part that is content (DOP-654): the person's unsaved
 * edits live in this browser, so the server cannot re-read them. It is sent
 * only when there are some, and is staged as the person's own typing.
 */

import type { DraftSummary } from "./memoDraftSummary";
import { memoVersionText } from "./memoVersionLabel";

export interface DealScreeningContext {
	/** The run's public id. */
	run_id: string;
	/** The run page tab on screen when the question is sent. */
	tab: string;
	/** The memo section the user has open, when one is. */
	section_key: string | null;
	/** The person's unsaved edits to this version, when they have some (DOP-654). */
	draft?: DraftSummary;
}

/**
 * The run fields the rail reads. Structural, so the page's full run type
 * (`types/dealScreening.ts`, DOP-497) is accepted as it is.
 */
export interface DealScreeningRunRef {
	public_id: string;
	version: number;
	/** The name a person gave this version, when they did. */
	name?: string | null;
	/** The deal's name as this version carries it (DOP-659). */
	deal_name?: string | null;
	deal: { name: string };
}

export interface DealScreeningContextInput {
	run: DealScreeningRunRef;
	tab: string;
	/** The section key the user has open, when one is. */
	sectionKey?: string | null;
}

export function buildDealScreeningContext({
	run,
	tab,
	sectionKey = null,
}: DealScreeningContextInput): DealScreeningContext {
	return { run_id: run.public_id, tab, section_key: sectionKey };
}

/** The rail's context chip: what "this" means in every question sent. */
export function dealScreeningChipLabel(run: DealScreeningRunRef): string {
	return `${run.deal_name?.trim() || run.deal.name} · ${memoVersionText(run)}`;
}
